import {pt2px} from '@/utils/converter'
import React from 'react'
import Image from 'next/image'
import 'yet-another-react-lightbox/styles.css'
import {useRouter} from 'next/router'
import { useTranslation } from 'next-i18next'

const DEDICATION_IMAGE_PAGE = 24;
const DEDICATION_TEXT_PAGE = 23;

const formatImageUrl = (url: string | undefined) => {
  if (!url) return '/placeholder.png';
  
  // If it's already a full URL, return it
  if (url.startsWith('http')) return url;
  
  // Remove any leading slashes from the path
  const cleanPath = url.replace(/^\/+/, '');
  
  // Construct the CDN URL, ensuring no double slashes
  const cdnUrl = process.env.NEXT_PUBLIC_CDN_URL?.replace(/\/+$/, '') || '';
  return `${cdnUrl}/${cleanPath}`;
};

const useIsLargeDevice = (breakpoint: number) => {
  const [isLarge, setIsLarge] = React.useState(false);

    React.useEffect(() => {
      const checkSize = () => setIsLarge(window.innerWidth >= breakpoint);
      checkSize();
      window.addEventListener('resize', checkSize);
      return () => window.removeEventListener('resize', checkSize);
    }, [breakpoint]);

    return isLarge;
  };

interface Props {
  pages: any
  pages_predefined_texts: any
  pages_dedication_texts: any[]
  product: any
  inputDedication: any
  cover_pages_predefined_texts: any
  cover_pages: any
  setStep?: any
  setActiveOption?: any
  setDedicationModal?: any
  name?:any
  dedicationImage?:any
  imageSelected?:any
  bookView?: string
  isProductRtlInBook?: any
  isInIsrael?: boolean
  isEditMode?: boolean // Add this prop
}
export default function BookMobileGlobal({
  pages,
  pages_predefined_texts,
  product,
  pages_dedication_texts,
  inputDedication,
  cover_pages,
  cover_pages_predefined_texts,
  setStep,
  setActiveOption,
  setDedicationModal,
  name,
  dedicationImage,
  imageSelected,
  bookView='single',
  isProductRtlInBook,
  isInIsrael,
  isEditMode = false // Add this prop
}: Props) {
  const {t} = useTranslation('personalizeModal');
  const isLarge = useIsLargeDevice(1024);
  const modified_pages = React.useMemo(() => {
    if (!pages) return [];

    const result: { src: string; logicalPage: number }[] = [];
    const reversedPages = [...pages].reverse();
    const reversedPagesLength = reversedPages.length;

    for (let i = 0; i < reversedPagesLength; i++) {
        result.push({
        src: reversedPages[i],
        logicalPage: reversedPagesLength - i - 1, // ORIGINAL page number
        });
    }

    // Reverse only for GLOBAL view
    if (!isInIsrael && !isEditMode) {
        result.reverse();
    }

    return result;
    }, [pages, isInIsrael, isEditMode]);

  const [imageWidth, setImageWidth] = React.useState(0)
  const [open, setOpen] = React.useState(true)
  const firstCoverPage = isProductRtlInBook ? '0' : '0';
  const lastCoverPage = isProductRtlInBook ? '1' : '1';
  const [allImagesLoaded, setAllImagesLoaded] = React.useState(false);
  const shouldFlipPages = !isInIsrael && !isEditMode; // Don't flip in edit mode
  const {locale} = useRouter();

  const router = useRouter()
  const isNotebookRoute = router.pathname.includes('notebook')
  const handleImageLoad = (event: any) => {
    const width = event.target.clientWidth
    setImageWidth(width)
  }
  const slides = [
    {
      type: 'custom-slide',
    },
  ]
  const [page, setPage] = React.useState(0)
  const [scrollPosition, setScrollPosition] = React.useState(0)

   // Calculate effective image selection state
  const hasPage1Dedication = pages_dedication_texts?.some((x: any) => x.page === '1') || false;
  const hasPage2Dedication = pages_dedication_texts?.some((x: any) => x.page === '2') || false;
  const hasOnlyOneDedicationPage = (hasPage1Dedication && !hasPage2Dedication) || (!hasPage1Dedication && hasPage2Dedication);
  // If only one dedication page exists, force text-only mode and ensure we render the existing page
  const singleDedicationIndex = hasOnlyOneDedicationPage ? (hasPage1Dedication ? 24 : 23) : null;
  const effectiveImageSelected = hasOnlyOneDedicationPage ? 'no' : imageSelected;
  const showDedicationImage = !hasOnlyOneDedicationPage && effectiveImageSelected === 'yes' && !!dedicationImage;

  const mobile_book_width =
    (document.getElementById('page-0')?.getBoundingClientRect().width ?? 0) + 25

  // Preload all images before showing the book
  React.useEffect(() => {
    const preloadImages = async () => {
      try {
        const imageUrls = [];
        
        // Check if cover_pages exists and has items before adding them
        if (cover_pages && cover_pages.length >= 2) {
          // Add cover images safely
          if (cover_pages[1]) imageUrls.push(formatImageUrl(cover_pages[1]));
          if (cover_pages[0]) imageUrls.push(formatImageUrl(cover_pages[0]));
        } else if (cover_pages && cover_pages.length === 1) {
          // If only one cover page exists, add it
          if (cover_pages[0]) imageUrls.push(formatImageUrl(cover_pages[0]));
        }
        
        // Add dedication image if needed
        if (showDedicationImage && dedicationImage) {
          imageUrls.push(dedicationImage);
        }
        
        // Preload all images using the browser's Image constructor
        await Promise.all(
          imageUrls.map(url => {
            return new Promise((resolve, reject) => {
              // Use the browser's Image constructor, not Next.js's
              const img = new (window as any).Image();
              img.src = url;
              img.onload = resolve;
              img.onerror = reject;
            });
          })
        );
        
        setAllImagesLoaded(true);
      } catch (error) {
        console.error('Error preloading images:', error);
        setAllImagesLoaded(true); // Still show the book even if some images fail to load
      }
    };
    
    preloadImages();
  }, [pages, cover_pages, dedicationImage, showDedicationImage, firstCoverPage, lastCoverPage, isEditMode]);

  React.useEffect(() => {
    if (!allImagesLoaded) return;

    // Function to handle the scroll event on the 'mobile-book' div
    const handleScroll = () => {
      // Get the reference to the 'mobile-book' div
      const mobileBookDiv = document.getElementById('mobile-book')

      // Update the state with the current scrollLeft position of the 'mobile-book' div
      if (mobileBookDiv) {
        let new_page: number = Math.floor(
          mobileBookDiv.scrollLeft / mobile_book_width,
        )
        if (new_page != 0) {
          new_page *= -1
        }
        setPage(new_page)
        setScrollPosition(mobileBookDiv.scrollLeft)
      }
    }

    // Add event listener for the scroll event on the 'mobile-book' div
    const mobileBookDiv = document.getElementById('mobile-book')
    if (mobileBookDiv) {
      mobileBookDiv.addEventListener('scroll', handleScroll)
    }

    // Clean up the event listener when the component unmounts
    return () => {
      if (mobileBookDiv) {
        mobileBookDiv.removeEventListener('scroll', handleScroll)
      }
    }
  }, [mobile_book_width, allImagesLoaded]) // Re-run if page width changes or images are loaded

  if (!allImagesLoaded) {
    return (
      <div className='flex-1 overflow-y-auto px-4 py-6 bg-[#FFFCEB] flex items-center justify-center gap-4 min-h-[75vh]'>
        <div className="w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        <h2 className="text-lg font-semibold leading-none">{t('bookLoadingText')}</h2>
      </div>
    );
  }

  if (pages) {
    //modified_pages.splice(modified_pages, 1)
    const getTextAlignment = (text_align: string) => {
      if (text_align == 'C') {
        return 'center'
      }
      if (text_align == 'R') {
        return 'right'
      }
      if (text_align == 'L') {
        return 'left'
      }
      return 'left'
    }
    // Helper function to safely get dimensions
    const getDimensions = (textDataArray: any[], pageNum: string) => {
      const pageData = textDataArray?.filter((x: any) => x.page == pageNum)[0];
      if (!pageData || !pageData.dimensions) {
        return { width: 100, height: 100 }; // Default dimensions
      }
      try {
        return JSON.parse(pageData.dimensions);
      } catch (e) {
        return { width: 100, height: 100 };
      }
    };

    const shouldRenderDedicationPage = (pageNum: number): boolean => {
    // only dedication pages
    if (pageNum !== DEDICATION_IMAGE_PAGE && pageNum !== DEDICATION_TEXT_PAGE) {
      return true; // non-dedication pages always render
    }

    // Only one dedication page exists in data
    if (singleDedicationIndex !== null) {
      return pageNum === singleDedicationIndex;
    }

    // Both pages exist
    if (showDedicationImage) {
      // show image page only
      return pageNum === DEDICATION_IMAGE_PAGE;
    }

    // text-only mode
    return pageNum === DEDICATION_TEXT_PAGE;
  };
    return (
      <div className='w-full'>
        <div className="mobile-book" id="mobile-book" key={`book-mobile-${bookView}-${isEditMode}-${showDedicationImage}`}>
          {/*
           *<Lightbox
           *  open={open}
           *  close={() => setOpen(false)}
           *  slides={slides}
           *  render={{
           *    slide: (slide: any) =>
           *      slide.type === 'custom-slide' ? (
           *        <BookMobileLightBoxSlide
           *          slide={slide}
           *          pages_predefined_texts={pages_predefined_texts}
           *          pages_dedication_texts={pages_dedication_texts}
           *          inputDedication={inputDedication}
           *          imageWidth={imageWidth}
           *        />
           *      ) : undefined,
           *  }}
           */}

          <div className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px] start-cover" id="page-0">
            {cover_pages_predefined_texts
              ?.filter((x: any) => x.page == ('0'))[0]
              ?.predefined_texts?.map((item: any, idx: number) => {
                let dimensions = JSON.parse(
                  cover_pages_predefined_texts.filter(
                    (x: any) => x.page == ('0'),
                  )[0]['dimensions'],
                )
                return (
                  <div
                    className={`texts absolute ${isNotebookRoute ? 'z-[10]' : 'z-[120]'}`}
                    key={`mobile-text-${idx}-${bookView}`}
                    id={item.X_coord_percent}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) /
                          pt2px(dimensions.width) <
                        10
                          ? 8
                          : (imageWidth * item.font_size) /
                            pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      whiteSpace: 'pre-wrap',
                      left: item.X_coord_percent * 2 + '%',
                      color: item.color,
                      fontFamily: item.font_face + ',sans-serif',
                    }}
                    dangerouslySetInnerHTML={{__html: item.text}}></div>
                )
              })}
            <div className={`w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px] ${isNotebookRoute ? 'z-[5]' : 'z-[110]'}`}>
              <Image
                alt="cover"
                className="object-cover border-[1.5px] border-primary rounded-md p-1"
                fill
                priority={true}
                onLoad={handleImageLoad}
                src={formatImageUrl(cover_pages[0])}
              />
            </div>
          </div>
          {/* KEY FIX: Conditionally reverse the pages for display */}
          {modified_pages?.map((page: any, index: number) => {
            // if (index == 0) {
            //   return <></>
            // }
            const textPageNumber = String(page.logicalPage);
            const pageNum = page.logicalPage;
            const isDedicationPage = pageNum === 23 || pageNum === 24;
            if(pageNum === 23 || pageNum === 24)
            {
              const dedicationPageData = pages_dedication_texts?.find(
                (x: any) => x.page === textPageNumber
              );

              const predefinedPageData = pages_predefined_texts?.find(
                (x: any) => x.page === textPageNumber
              );

              if (!shouldRenderDedicationPage(pageNum)) {
                  return null;
                }
                return(
                <div className={`relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px]`} id={`page-mobile-${index}`} key={index}>
                  <div className='w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px]'>
                    <Image
                      alt="cover"
                      className={`block w-full h-full mob-book-image img-${index} border-[1.5px] border-primary rounded-md p-1`}
                      fill
                      priority={true}
                      onLoad={handleImageLoad}
                      key={index}
                      // src={DedicationBackground}
                      src={formatImageUrl(page.src)}
                    />
                  </div>
                {dedicationPageData?.dedication_texts?.map((item: any, idx: number)=> {
                    let dimensions = getDimensions(pages_dedication_texts, textPageNumber);

                    let book_img = document.querySelector('.mob-book-image')
                    if (item) {
                      const isImageBlock = !item.text; // last item is for image

                      const top = item.Y_coord_percent > 0
                        ? `${item.Y_coord_percent}%`
                        : `${(item.Y_coord / dimensions.height) * 71}%`;
                      const left = item.X_coord_percent > 0
                        ? `${item.X_coord_percent}%`
                        : `${(item.X_coord / dimensions.height) * 85}%`;
                      const imageTop = isLarge ? `${(item.Y_coord / dimensions.height) * 77.3}%` : `${(item.Y_coord / dimensions.height) * 78}%`;
                      const imageLeft = isLarge ? `${(item.X_coord / dimensions.height) * 74.6}%` : `${(item.X_coord / dimensions.height) * 74.4}%`;

                      const width = (imageWidth * item.max_width) / pt2px(dimensions.width);

                      const maxwidth = item.max_width
                      
                      if (isImageBlock) {
                        // Skip image block when we are in single-page mode or when no image is selected
                        if (!showDedicationImage) return null;
                        return (
                          <div
                            key={`image-${idx}`}
                            className="texts absolute z-[20]"
                            style={{
                              top: isImageBlock ? imageTop : top,
                              left: isImageBlock ? imageLeft : left,
                              width: width,
                            }}
                          >
                            {dedicationImage && (
                              <img
                                src={dedicationImage}
                                alt="Child"
                                className="object-cover rounded-full"
                              />
                            )}
                          </div>
                        );
                      }

                      const maxWidth =
                        item.max_width && item.max_width > 0
                        ? `${item.max_width}px` 
                        : `${item.width_percent}%`;
                      return (
                          <div
                          key={`mobile-text-${idx}-${bookView}`}
                          className="texts absolute z-[20]"
                          style={{
                            fontSize:
                              (imageWidth * item.font_size) /
                                pt2px(dimensions.width) <
                              10
                                ? 8
                                : (imageWidth * item.font_size) /
                                  pt2px(dimensions.width),
                            textAlign: getTextAlignment(item.text_align),
                            width:
                              (imageWidth * item.max_width) / pt2px(dimensions.width),
                            top: top,
                            lineHeight: item.line_height,
                            whiteSpace: 'pre-wrap',
                            left: left,
                            color: item.color,
                            fontFamily: item.font_face + ',sans-serif',
                            maxWidth: maxWidth
                          }}
                        >
                          { item.text === 'أدخل النص' ? inputDedication.replace(
                                  '{basmti}',
                                  name ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
                                ) : item.text?.includes('{basmti}')
                            ? item.text.replace('{basmti}', name ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en)
                            : item.text}
                        </div>
                      )
                    } else{
                      return (
                        <></>
                      )
                    }
                  })}
                {predefinedPageData?.predefined_texts?.map((item: any, idx: number) => {
                    let dimensions = getDimensions(pages_predefined_texts, textPageNumber);
                    let book_img = document.querySelector('.mob-book-image')
                    if (item) {
                      return (
                        <div
                          className="texts absolute"
                          key={`mobile-text-${idx}-${bookView}`}
                          style={{
                            //fontSize:
                            //(imageWidth * item.font_size) /
                            //pt2px(dimensions.width) <
                            //10
                            //? 12
                            //: (imageWidth * item.font_size) /
                            //pt2px(dimensions.width),

                            fontSize:
                              (imageWidth * item.font_size) /
                              pt2px(dimensions.width),
                            width:
                              (imageWidth * item.max_width) /
                              pt2px(dimensions.width),
                            top: item.Y_coord_percent + '%',
                            textAlign: getTextAlignment(item.text_align),
                            lineHeight: item.line_height,
                            whiteSpace: 'pre-wrap',
                            color: item.color,
                            left: item.X_coord_percent + '%',
                            fontFamily: item.font_face + ',sans-serif',
                          }}
                          dangerouslySetInnerHTML={{__html: item.text}}></div>
                      )
                    } else {
                      return <></>
                    }
                  })}
                  {inputDedication === '' && (
                    <>
                    <div className='absolute -top-6 left-1/2 -translate-x-1/2 bg-primary text-white text-center p-4 h-[52px] w-contain rounded-lg text-sm cursor-pointer lg:hidden' onClick={()=>{
                      setStep('edit');
                      setActiveOption('dedication');
                    }}>
                      <p>{t('addDedicationText')}</p>
                    </div>
                    <div className='hidden lg:block absolute top-10 left-1/2 -translate-x-1/2 bg-primary text-white text-center p-4 h-[52px] w-contain rounded-lg cursor-pointer text-sm' onClick={()=>{
                      setDedicationModal(true);
                    }}>
                      <p>{t('addDedicationText')}</p>
                    </div>
                    </>
                  )}
                  </div>
                )
            }
            else if(pageNum !== 1 && pageNum !== 2) {
              const dedicationPageData = pages_dedication_texts?.filter((x: any) => x.page == textPageNumber)[0];
              const predefinedPageData = pages_predefined_texts?.filter((x: any) => x.page == textPageNumber)[0];
              return (
              <div className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px]" id={`page-mobile-${index}`} key={index}>
                <div className='w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px]'>
                  <Image
                    alt="cover"
                    className={`block w-full mob-book-image img-${index} border-[1.5px] border-primary rounded-md p-1`}
                    fill
                    priority={true}
                    onLoad={handleImageLoad}
                    key={index}
                    src={formatImageUrl(page.src)}
                  />
                </div>
                {dedicationPageData?.dedication_texts?.map((item: any, idx: number) =>{
                    let dimensions = getDimensions(pages_dedication_texts, textPageNumber);
                    let book_img = document.querySelector('.mob-book-image')
                    if (item) {
                      return (
                            <div
                              className={`texts absolute ${(index === 1 && inputDedication == '') ? 'mt-10': ''}`}
                              key={`mobile-text-${idx}-${bookView}`}
                              style={{
                                fontSize:
                                  (imageWidth * item.font_size) /
                                    pt2px(dimensions.width) <
                                  10
                                    ? 8
                                    : (imageWidth * item.font_size) /
                                      pt2px(dimensions.width),
                                width:
                                  (imageWidth * item.max_width) /
                                  pt2px(dimensions.width),
                                top: item.Y_coord_percent + '%',
                                whiteSpace: 'pre-wrap',
                                textAlign: getTextAlignment(item.text_align),
                                lineHeight: item.line_height,
                                color: item.color,
                                left: item.X_coord_percent + '%',
                                fontFamily: item.font_face + ',sans-serif',
                                direction: /^[a-zA-Z0-9\s.,!?;:'"\-()]*$/.test(inputDedication) ? 'ltr' : undefined,
                              }}>
                                {inputDedication.replace(
                                  '{basmti}',
                                  name ?? locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
                                )}
                            </div>
                      )
                    } else{
                      return (
                        <></>
                      )
                    }
                  })}
                {predefinedPageData?.predefined_texts?.map((item: any, idx: number) => {
                    let dimensions = getDimensions(pages_predefined_texts, textPageNumber);
                    let book_img = document.querySelector('.mob-book-image')
                    if (item) {
                      return (
                        <div
                          className="texts absolute"
                          key={`mobile-text-${idx}-${bookView}`}
                          style={{
                            //fontSize:
                            //(imageWidth * item.font_size) /
                            //pt2px(dimensions.width) <
                            //10
                            //? 12
                            //: (imageWidth * item.font_size) /
                            //pt2px(dimensions.width),

                            fontSize:
                              (imageWidth * item.font_size) /
                              pt2px(dimensions.width),
                            width:
                              (imageWidth * item.max_width) /
                              pt2px(dimensions.width),
                            top: item.Y_coord_percent + '%',
                            textAlign: getTextAlignment(item.text_align),
                            lineHeight: item.line_height,
                            whiteSpace: 'pre-wrap',
                            color: item.color,
                            left: item.X_coord_percent + '%',
                            fontFamily: item.font_face + ',sans-serif',
                          }}
                          dangerouslySetInnerHTML={{__html: item.text}}></div>
                      )
                    } else {
                      return <></>
                    }
                  })}
                  {(inputDedication === '' && index === 1) && (
                    <>
                    <button className='bg-primary-500 py-4 px-6 absolute -top-4 right-0 left-0 mx-auto rounded-lg max-w-[150px] flex lg:hidden items-center justify-center' onClick={()=>{
                      setStep('edit');
                      setActiveOption('dedication')
                    }}>
                      <p className='font-medium text-white text-base'>اكتب اهداء</p>
                    </button>
                    <button className='bg-primary-500 py-4 px-6 absolute -top-4 right-0 left-0 mx-auto rounded-lg max-w-[150px] hidden lg:flex items-center justify-center' onClick={()=>{
                      setDedicationModal(true);
                    }}>
                      <p className='font-medium text-white text-base'>اكتب اهداء</p>
                    </button>
                    </>
                  )}
              </div>
            )
            }
          })}

          <div className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px] mb-72 scroll-mb-96" id={`page-mobile-${modified_pages.length + 1}`}>
            {cover_pages_predefined_texts
              ?.filter((x: any) => x.page == (isProductRtlInBook ? '0' : '1'))[0]
              ?.predefined_texts?.map((item: any, idx: number) => {
                let dimensions = JSON.parse(
                  cover_pages_predefined_texts.filter(
                    (x: any) => x.page == (isProductRtlInBook ? '0' : '1'),
                  )[0]['dimensions'],
                )
                return (
                  <div
                    className="texts absolute"
                    key={`mobile-text-${idx}-${bookView}`}
                    id={item.X_coord_percent}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) /
                          pt2px(dimensions.width) <
                        10
                          ? 8
                          : (imageWidth * item.font_size) /
                            pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      left: item.X_coord_percent * 2 + '%',
                      whiteSpace: 'pre-wrap',
                      color: item.color,
                      fontFamily: item.font_face + ',sans-serif',
                    }}
                    dangerouslySetInnerHTML={{__html: item.text}}></div>
                )
              })}
            <div className=' w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px] '>
              <Image
                alt="cover"
                className="object-cover border-[1.5px] border-primary rounded-md p-1"
                fill
                priority={true}
                onLoad={handleImageLoad}
                src={formatImageUrl(cover_pages[1])}
                unoptimized
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
  return <></>
}